import type { CreateOrganisationOperatorData } from '@/api/resources/organisation-operators'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { machinesQueryOptions } from '@/api/resources/machines'
import { createOrganisationOperator } from '@/api/resources/organisation-operators'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AddOperatorForm } from '@/components/users/add-operator-form'
import { useRouteModal } from '@/hooks/use-route-modal'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'

const createOperatorSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.email('Please enter a valid email address'),
  email_optin: z.boolean().optional(),
  machine_ids: z.array(z.number()),
})

export const Route = createFileRoute('/_app/users/$organisationId/operators/new')({
  head: () => ({ meta: [{ title: 'Add Operator - MyJunea' }] }),
  component: AddOrganisationOperatorRoute,
})

function AddOrganisationOperatorRoute(): React.ReactElement {
  const { organisationId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const organisationIdNum = Number(organisationId)

  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({
        to: '/users/$organisationId/operators',
        params: { organisationId },
        resetScroll: false,
      }),
  )

  const { data: machines } = useQuery(
    machinesQueryOptions({ organisation_id: [organisationIdNum] }),
  )

  const createMutation = useMutation({
    mutationFn: async (data: CreateOrganisationOperatorData) =>
      createOrganisationOperator(organisationIdNum, data),
    onSuccess: () => {
      clearServerErrors()
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationOperators.all(organisationIdNum),
      })
      toast.success('Operator created successfully')
      handleClose()
    },
    onError: handleMutationError,
  })

  const form = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      email_optin: false,
      machine_ids: [],
    } as CreateOrganisationOperatorData,
    validators: { onSubmit: createOperatorSchema },
    onSubmit: ({ value }) => createMutation.mutate(value),
  })

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    clearServerErrors()
    createMutation.reset()
    void form.handleSubmit()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add operator</DialogTitle>
          <DialogDescription>
            Create a new operator and assign them to one or more machines.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <AddOperatorForm form={form} machines={machines} serverErrors={serverErrors} />

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
